import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  IoClose, IoSpeedometer, IoCalendar, IoSettings, IoSave, IoTrash, IoAdd, 
  IoDocument, IoLocationSharp, IoAttach, IoCheckmarkCircle, IoWarning,
  IoTime, IoCard, IoShield, IoConstruct, IoNavigate
} from 'react-icons/io5';
import { 
  FaCarSide, FaTools, FaHistory, FaIdCard, FaFileAlt, FaShieldAlt, 
  FaEdit, FaPlus, FaTimes, FaTrash, FaFileUpload, FaFile, FaGasPump, 
  FaSatelliteDish, FaExclamationTriangle, FaCheckCircle, FaClock,
  FaDownload, FaEye, FaCalendarAlt, FaMapMarkerAlt, FaCog, FaInfo
} from 'react-icons/fa';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import moment from 'moment';
import { cars } from '../../services/api';

const VehicleDetailsModal = ({ 
  vehicle,
  car, 
  isOpen, 
  onClose,
  onUpdate
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);
  
  // Service management states
  const [isAddingService, setIsAddingService] = useState(false);
  const [isEditingService, setIsEditingService] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    type: '',
    date: new Date(),
    mileage: '',
    description: '',
    cost: '',
  });
  
  // Forms for various sections
  const [kteoForm, setKteoForm] = useState({
    expiryDate: new Date(new Date().setFullYear(new Date().getFullYear() + 2)),
    certificateNumber: '',
    lastInspectionDate: new Date(),
    nextInspectionDate: new Date(new Date().setFullYear(new Date().getFullYear() + 2)),
    status: 'valid'
  });

  const [emissionForm, setEmissionForm] = useState({
    expiryDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
    certificateNumber: '',
    issueDate: new Date(),
    status: 'valid'
  });

  const [gpsForm, setGpsForm] = useState({
    installed: false,
    deviceId: '',
    model: '',
    installationDate: new Date()
  });

  const [attachmentForm, setAttachmentForm] = useState({
    file: null,
    description: '',
    category: 'other'
  });
  
  const [insuranceForm, setInsuranceForm] = useState({
    provider: '',
    policyNumber: '',
    startDate: new Date(),
    endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
    type: 'normal',
    coverageAmount: 0
  });

  // UI states
  const [isEditingInsurance, setIsEditingInsurance] = useState(false);
  const [isEditingKteo, setIsEditingKteo] = useState(false);
  const [isEditingEmission, setIsEditingEmission] = useState(false);
  const [isEditingGps, setIsEditingGps] = useState(false);
  const [isAddingAttachment, setIsAddingAttachment] = useState(false);
  const [isEditingAttachment, setIsEditingAttachment] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);

  // Calculated values
  const isInsuranceValid = vehicle?.insurance?.endDate ? moment(vehicle.insurance.endDate).isAfter(moment()) : false;
  const isTempInsurance = vehicle?.insurance?.type === 'temp';
  const isKteoValid = vehicle?.kteo?.expiryDate ? moment(vehicle.kteo.expiryDate).isAfter(moment()) : false;
  const isEmissionValid = vehicle?.emissionCard?.expiryDate ? moment(vehicle.emissionCard.expiryDate).isAfter(moment()) : false;
  const isGpsInstalled = vehicle?.gps?.installed || false;
  
  const lastServiceRecord = serviceHistory.length > 0 
    ? serviceHistory.sort((a, b) => new Date(b.date) - new Date(a.date))[0] 
    : null;
  const daysSinceLastService = lastServiceRecord 
    ? moment().diff(moment(lastServiceRecord.date), 'days') 
    : null;

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: FaIdCard },
    { id: 'documents', label: 'Documents', icon: IoDocument },
    { id: 'service', label: 'Service History', icon: FaTools },
    { id: 'attachments', label: 'Attachments', icon: IoAttach }
  ];

  // Animation variants
  const tabContentVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.2 } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2 } }
  };

  // Fetch service history and attachments when the modal opens
  useEffect(() => {
    if (isOpen && vehicle && vehicle._id) {
      fetchServiceHistory();
      fetchAttachments();
      
      // Initialize forms with existing data
      if (vehicle.insurance) {
        setInsuranceForm({
          provider: vehicle.insurance.provider || '',
          policyNumber: vehicle.insurance.policyNumber || '',
          startDate: vehicle.insurance.startDate ? new Date(vehicle.insurance.startDate) : new Date(),
          endDate: vehicle.insurance.endDate ? new Date(vehicle.insurance.endDate) : new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
          type: vehicle.insurance.type || 'normal',
          coverageAmount: vehicle.insurance.coverageAmount || 0
        });
      }

      if (vehicle.kteo) {
        setKteoForm({
          expiryDate: vehicle.kteo.expiryDate ? new Date(vehicle.kteo.expiryDate) : new Date(new Date().setFullYear(new Date().getFullYear() + 2)),
          certificateNumber: vehicle.kteo.certificateNumber || '',
          lastInspectionDate: vehicle.kteo.lastInspectionDate ? new Date(vehicle.kteo.lastInspectionDate) : new Date(),
          nextInspectionDate: vehicle.kteo.nextInspectionDate ? new Date(vehicle.kteo.nextInspectionDate) : new Date(new Date().setFullYear(new Date().getFullYear() + 2)),
          status: vehicle.kteo.status || 'valid'
        });
      }

      if (vehicle.emissionCard) {
        setEmissionForm({
          expiryDate: vehicle.emissionCard.expiryDate ? new Date(vehicle.emissionCard.expiryDate) : new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
          certificateNumber: vehicle.emissionCard.certificateNumber || '',
          issueDate: vehicle.emissionCard.issueDate ? new Date(vehicle.emissionCard.issueDate) : new Date(),
          status: vehicle.emissionCard.status || 'valid'
        });
      }

      if (vehicle.gps) {
        setGpsForm({
          installed: vehicle.gps.installed || false,
          deviceId: vehicle.gps.deviceId || '',
          model: vehicle.gps.model || '',
          installationDate: vehicle.gps.installationDate ? new Date(vehicle.gps.installationDate) : new Date()
        });
      }
    }
    
    // Reset forms and states when modal closes
    return () => {
      setIsAddingService(false);
      setIsEditingService(false);
      setIsEditingInsurance(false);
      setIsEditingKteo(false);
      setIsEditingEmission(false);
      setIsEditingGps(false);
      setSelectedService(null);
      setError(null);
      setSuccessMessage(null);
    };
  }, [isOpen, vehicle]);

  const fetchServiceHistory = async () => {
    if (!vehicle || !vehicle._id) return;
    
    setLoading(true);
    try {
      const response = await cars.getServiceHistory(car._id, vehicle._id);
      setServiceHistory(response.data || []);
    } catch (err) {
      console.error('Error fetching service history:', err);
      setError('Failed to load service history');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAttachments = async () => {
    if (!vehicle || !vehicle._id) return;
    
    try {
      const response = await cars.getAttachments(car._id, vehicle._id);
      setAttachments(response.data || []);
    } catch (err) {
      console.error('Error fetching attachments:', err);
      setError('Failed to load attachments');
    }
  };

  // Reset service form
  const resetServiceForm = () => {
    setServiceForm({
      type: '',
      date: new Date(),
      mileage: '',
      description: '',
      cost: '',
    });
  };
  
  // Initialize service form for editing
  const editService = (service) => {
    setSelectedService(service);
    setServiceForm({
      type: service.type || '',
      date: service.date ? new Date(service.date) : new Date(),
      mileage: service.mileage || '',
      description: service.description || '',
      cost: service.cost || '',
    });
    setIsEditingService(true);
    setIsAddingService(false);
  };
  
  // Handle service form input changes
  const handleServiceFormChange = (e) => {
    const { name, value } = e.target;
    setServiceForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle date change for service form
  const handleServiceDateChange = (date) => {
    setServiceForm(prev => ({
      ...prev,
      date
    }));
  };
  
  // Handle insurance form input changes
  const handleInsuranceFormChange = (e) => {
    const { name, value } = e.target;
    setInsuranceForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle date changes for insurance form
  const handleInsuranceDateChange = (field, date) => {
    setInsuranceForm(prev => ({
      ...prev,
      [field]: date
    }));
  };
  
  // Handle KTEO form input changes
  const handleKteoFormChange = (e) => {
    const { name, value } = e.target;
    setKteoForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle emission form input changes
  const handleEmissionFormChange = (e) => {
    const { name, value } = e.target;
    setEmissionForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle GPS form input changes
  const handleGpsFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setGpsForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Add new service record
  const addServiceRecord = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const formData = new FormData();
      formData.append('type', serviceForm.type);
      formData.append('date', serviceForm.date.toISOString());
      formData.append('mileage', serviceForm.mileage);
      formData.append('description', serviceForm.description);
      formData.append('cost', serviceForm.cost);
      
      await cars.addServiceRecord(car._id, vehicle._id, formData);
      setSuccessMessage('Service record added successfully');
      resetServiceForm();
      setIsAddingService(false);
      await fetchServiceHistory();
    } catch (err) {
      console.error('Error adding service record:', err);
      setError('Failed to add service record: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  // Update existing service record
  const updateServiceRecord = async () => {
    if (!selectedService?._id) return;
    
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const serviceData = {
        type: serviceForm.type,
        date: serviceForm.date.toISOString(),
        mileage: serviceForm.mileage,
        description: serviceForm.description,
        cost: serviceForm.cost,
      };
      
      await cars.updateServiceRecord(car._id, vehicle._id, selectedService._id, serviceData);
      setSuccessMessage('Service record updated successfully');
      resetServiceForm();
      setIsEditingService(false);
      setSelectedService(null);
      await fetchServiceHistory();
    } catch (err) {
      console.error('Error updating service record:', err);
      setError('Failed to update service record: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  // Delete service record
  const deleteServiceRecord = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service record? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      await cars.deleteServiceRecord(car._id, vehicle._id, serviceId);
      setSuccessMessage('Service record deleted successfully');
      await fetchServiceHistory();
    } catch (err) {
      console.error('Error deleting service record:', err);
      setError('Failed to delete service record: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  // Update vehicle insurance
  const updateInsurance = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const insuranceData = {
        provider: insuranceForm.provider,
        policyNumber: insuranceForm.policyNumber,
        startDate: insuranceForm.startDate.toISOString(),
        endDate: insuranceForm.endDate.toISOString(),
        type: insuranceForm.type,
        coverageAmount: insuranceForm.type === 'mixed' ? insuranceForm.coverageAmount : 0
      };
      
      await cars.updateInsurance(car._id, vehicle._id, insuranceData);
      setSuccessMessage('Insurance information updated successfully');
      setIsEditingInsurance(false);
      
      // Update local vehicle data instead of triggering parent refresh
      vehicle.insurance = insuranceData;
      
      // Only call onUpdate for critical updates that affect vehicle status
      // if (typeof onUpdate === 'function') {
      //   onUpdate('insurance', 'update');
      // }
    } catch (err) {
      console.error('Error updating insurance:', err);
      setError('Failed to update insurance: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Update vehicle KTEO
  const updateKteo = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const kteoData = {
        expiryDate: kteoForm.expiryDate.toISOString(),
        certificateNumber: kteoForm.certificateNumber,
        lastInspectionDate: kteoForm.lastInspectionDate.toISOString(),
        nextInspectionDate: kteoForm.nextInspectionDate.toISOString(),
        status: kteoForm.status
      };
      
      await cars.updateKteo(car._id, vehicle._id, kteoData);
      setSuccessMessage('KTEO information updated successfully');
      setIsEditingKteo(false);
      
      // Update local vehicle data instead of triggering parent refresh
      vehicle.kteo = kteoData;
      
      // Only call onUpdate for critical updates that affect vehicle status
      // if (typeof onUpdate === 'function') {
      //   onUpdate('kteo', 'update');
      // }
    } catch (err) {
      console.error('Error updating KTEO:', err);
      setError('Failed to update KTEO: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Update vehicle emission card
  const updateEmission = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const emissionData = {
        expiryDate: emissionForm.expiryDate.toISOString(),
        certificateNumber: emissionForm.certificateNumber,
        issueDate: emissionForm.issueDate.toISOString(),
        status: emissionForm.status
      };
      
      await cars.updateEmissionCard(car._id, vehicle._id, emissionData);
      setSuccessMessage('Emission card information updated successfully');
      setIsEditingEmission(false);
      
      // Update local vehicle data instead of triggering parent refresh
      vehicle.emissionCard = emissionData;
      
      // Only call onUpdate for critical updates that affect vehicle status
      // if (typeof onUpdate === 'function') {
      //   onUpdate('emission', 'update');
      // }
    } catch (err) {
      console.error('Error updating emission card:', err);
      setError('Failed to update emission card: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Update vehicle GPS
  const updateGps = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const gpsData = {
        installed: gpsForm.installed,
        deviceId: gpsForm.deviceId,
        model: gpsForm.model,
        installationDate: gpsForm.installationDate.toISOString()
      };
      
      await cars.updateGps(car._id, vehicle._id, gpsData);
      setSuccessMessage('GPS information updated successfully');
      setIsEditingGps(false);
      
      // Update local vehicle data instead of triggering parent refresh
      vehicle.gps = gpsData;
      
      // Only call onUpdate for critical updates that affect vehicle status
      // if (typeof onUpdate === 'function') {
      //   onUpdate('gps', 'update');
      // }
    } catch (err) {
      console.error('Error updating GPS:', err);
      setError('Failed to update GPS: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Initialize attachment form for editing
  const editAttachment = (attachment) => {
    setSelectedAttachment(attachment);
    setAttachmentForm({
      file: null, // Don't prefill file for editing
      description: attachment.description || '',
      category: attachment.category || 'other'
    });
    setIsEditingAttachment(true);
    setIsAddingAttachment(false);
  };

  // Delete attachment
  const deleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      await cars.deleteAttachment(car._id, vehicle._id, attachmentId);
      setSuccessMessage('Attachment deleted successfully');
      await fetchAttachments();
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setError('Failed to delete attachment: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Update existing attachment
  const updateAttachment = async () => {
    if (!selectedAttachment?._id) return;
    
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const formData = new FormData();
      if (attachmentForm.file) {
        formData.append('file', attachmentForm.file);
      }
      formData.append('description', attachmentForm.description);
      formData.append('category', attachmentForm.category);
      
      await cars.updateAttachment(car._id, vehicle._id, selectedAttachment._id, formData);
      setSuccessMessage('Attachment updated successfully');
      setIsEditingAttachment(false);
      setSelectedAttachment(null);
      await fetchAttachments();
    } catch (err) {
      console.error('Error updating attachment:', err);
      setError('Failed to update attachment: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Add new attachment
  const addAttachment = async () => {
    if (!attachmentForm.file) {
      setError('Please select a file to upload');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const formData = new FormData();
      formData.append('file', attachmentForm.file);
      formData.append('description', attachmentForm.description);
      formData.append('category', attachmentForm.category);
      
      await cars.uploadAttachment(car._id, vehicle._id, formData);
      setSuccessMessage('Attachment added successfully');
      
      setAttachmentForm({ file: null, description: '', category: 'other' });
      setIsAddingAttachment(false);
      
      await fetchAttachments();
    } catch (err) {
      console.error('Error adding attachment:', err);
      setError('Failed to add attachment: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  // Modal variant animations
  const modalVariant = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: 50, scale: 0.95, transition: { duration: 0.2 } }
  };
  
  const backdropVariant = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };
  
  // If no vehicle data, don't render
  if (!vehicle) return null;
  
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div 
            className="absolute inset-0 bg-black bg-opacity-60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{ backdropFilter: 'blur(8px)' }}
          />
          
          {/* Modal */}
          <motion.div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl mx-4 overflow-hidden"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ maxHeight: '90vh' }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                    <FaCarSide className="text-xl" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">
                      {car.make} {car.model} ({car.year})
                    </h2>
                    <p className="text-blue-100 text-sm">
                      License Plate: {vehicle.licensePlate} • VIN: {vehicle.vin}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={onClose}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <IoClose size={24} />
                </button>
              </div>
            </div>

            {/* Status Bar */}
            <div className="bg-gray-50 px-6 py-3 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    vehicle.status === 'available' ? 'bg-green-100 text-green-800' :
                    vehicle.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                    vehicle.status === 'rented' ? 'bg-blue-100 text-blue-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    <div className={`w-2 h-2 rounded-full mr-2 ${
                      vehicle.status === 'available' ? 'bg-green-500' :
                      vehicle.status === 'maintenance' ? 'bg-yellow-500' :
                      vehicle.status === 'rented' ? 'bg-blue-500' :
                      'bg-red-500'
                    }`} />
                    {vehicle.status.charAt(0).toUpperCase() + vehicle.status.slice(1)}
                  </span>
                  
                  {/* {vehicle.currentMileage && vehicle.currentMileage !== 'N/A' && vehicle.currentMileage > 0 && (
                    <div className="flex items-center text-sm text-gray-600">
                      <IoSpeedometer className="mr-1" />
                      {vehicle.currentMileage.toLocaleString()} km
                    </div>
                    
                  )} */}
                </div>

                {/* Quick Status Indicators */}
                <div className="flex items-center space-x-3">
                  <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
                    isTempInsurance ? 'bg-red-100 text-red-700' :
                    isInsuranceValid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    <IoShield className="mr-1" />
                    {isTempInsurance ? 'TEMP Insurance' : isInsuranceValid ? 'Insurance OK' : 'Insurance Expired'}
                  </div>
                  
                  <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
                    isKteoValid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    <IoDocument className="mr-1" />
                    {isKteoValid ? 'KTEO OK' : 'KTEO Expired'}
                  </div>

                  <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
                    isEmissionValid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    <FaGasPump className="mr-1" />
                    {isEmissionValid ? 'Emission OK' : 'Emission Expired'}
                  </div>

                  <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
                    isGpsInstalled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    <IoNavigate className="mr-1" />
                    {isGpsInstalled ? 'GPS OK' : 'No GPS'}
                  </div>
                  
                  <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
                    daysSinceLastService === null ? 'bg-gray-100 text-gray-700' :
                    daysSinceLastService > 180 ? 'bg-red-100 text-red-700' :
                    daysSinceLastService > 150 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    <IoConstruct className="mr-1" />
                    {daysSinceLastService === null ? 'No Service' :
                     daysSinceLastService > 180 ? 'Service Overdue' :
                     daysSinceLastService > 150 ? 'Service Due' : 'Service OK'}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white border-b">
              <nav className="flex px-6">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="text-base" />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
              <div className="p-6">
                {/* Messages */}
                <AnimatePresence>
                  {successMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center"
                    >
                      <IoCheckmarkCircle className="text-green-500 mr-2" />
                      <span className="text-green-700">{successMessage}</span>
                    </motion.div>
                  )}
                  
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center"
                    >
                      <IoWarning className="text-red-500 mr-2" />
                      <span className="text-red-700">{error}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Tab Content */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    variants={tabContentVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                      <div className="space-y-6">
                        {/* Vehicle Info Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                          {/* Basic Information */}
                          <motion.div
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
                          >
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                              <FaIdCard className="mr-2 text-blue-500" />
                              Vehicle Details
                            </h3>
                            <div className="space-y-3">
                              <div className="flex justify-between py-2 border-b border-gray-100">
                                <span className="text-gray-500">Category</span>
                                <span className="font-medium">{car.category}</span>
                              </div>
                              <div className="flex justify-between py-2 border-b border-gray-100">
                                <span className="text-gray-500">Condition</span>
                                <span className="font-medium capitalize">{vehicle.condition || 'Good'}</span>
                              </div>
                              <div className="flex justify-between py-2 border-b border-gray-100">
                                <span className="text-gray-500">Purchase Date</span>
                                <span className="font-medium">
                                  {vehicle.purchaseDate ? moment(vehicle.purchaseDate).format('DD/MM/YYYY') : 'N/A'}
                                </span>
                              </div>
                              <div className="flex justify-between py-2">
                                <span className="text-gray-500">Transmission</span>
                                <span className="font-medium capitalize">{car.transmission}</span>
                              </div>
                            </div>
                          </motion.div>

                          {/* Service Status */}
                          <motion.div
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            transition={{ delay: 0.1 }}
                            className={`border rounded-xl p-6 shadow-sm ${
                              daysSinceLastService === null ? 'bg-gray-50 border-gray-200' :
                              daysSinceLastService > 180 ? 'bg-red-50 border-red-200' :
                              daysSinceLastService > 150 ? 'bg-yellow-50 border-yellow-200' :
                              'bg-green-50 border-green-200'
                            }`}
                          >
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                              <FaTools className="mr-2 text-blue-500" />
                              Service Status
                            </h3>
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-gray-600">Status</span>
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                  daysSinceLastService === null ? 'bg-gray-100 text-gray-700' :
                                  daysSinceLastService > 180 ? 'bg-red-100 text-red-700' :
                                  daysSinceLastService > 150 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-green-100 text-green-700'
                                }`}>
                                  {daysSinceLastService === null ? 'No History' :
                                   daysSinceLastService > 180 ? 'Overdue' :
                                   daysSinceLastService > 150 ? 'Due Soon' : 'Up to Date'}
                                </span>
                              </div>
                              {lastServiceRecord && (
                                <>
                                  <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="text-gray-500">Last Service</span>
                                    <span className="font-medium">
                                      {moment(lastServiceRecord.date).format('DD/MM/YYYY')}
                                    </span>
                                  </div>
                                  <div className="flex justify-between py-2">
                                    <span className="text-gray-500">Days Ago</span>
                                    <span className="font-medium">{daysSinceLastService} days</span>
                                  </div>
                                </>
                              )}
                              <button
                                onClick={() => setActiveTab('service')}
                                className="w-full mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                View Service History
                              </button>
                            </div>
                          </motion.div>

                          {/* Quick Stats */}
                          <motion.div
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            transition={{ delay: 0.2 }}
                            className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
                          >
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                              <FaInfo className="mr-2 text-blue-500" />
                              Quick Stats
                            </h3>
                            <div className="space-y-4">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600">
                                  {serviceHistory.length}
                                </div>
                                <div className="text-sm text-gray-500">Service Records</div>
                              </div>
                              <div className="text-center">
                                <div className="text-2xl font-bold text-green-600">
                                  €{serviceHistory.reduce((sum, record) => sum + (parseFloat(record.cost) || 0), 0).toFixed(0)}
                                </div>
                                <div className="text-sm text-gray-500">Total Service Cost</div>
                              </div>
                              <div className="text-center">
                                <div className="text-2xl font-bold text-purple-600">
                                  {attachments.length}
                                </div>
                                <div className="text-sm text-gray-500">Attachments</div>
                              </div>
                            </div>
                          </motion.div>
                        </div>
                      </div>
                    )}

                    {/* Documents Tab */}
                    {activeTab === 'documents' && (
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Insurance Card */}
                          <motion.div
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            className={`border rounded-xl p-6 shadow-sm ${
                              isTempInsurance ? 'bg-red-50 border-red-200' :
                              isInsuranceValid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-semibold flex items-center">
                                <IoShield className="mr-2 text-blue-500" />
                                Insurance
                              </h3>
                              {!isEditingInsurance && (
                                <button
                                  onClick={() => setIsEditingInsurance(true)}
                                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                                >
                                  <FaEdit className="mr-1 inline" /> Edit
                                </button>
                              )}
                            </div>
                            
                            {vehicle.insurance ? (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">Status</span>
                                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                    isTempInsurance ? 'bg-red-100 text-red-700' :
                                    isInsuranceValid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {isTempInsurance ? 'TEMP - Update Required' :
                                     isInsuranceValid ? 'Valid' : 'Expired'}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-500">Provider</span>
                                    <div className="font-medium">{vehicle.insurance.provider}</div>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Expires</span>
                                    <div className="font-medium">
                                      {moment(vehicle.insurance.endDate).format('DD/MM/YYYY')}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <p className="text-gray-600">No insurance information</p>
                            )}

                            {/* Insurance Edit Form */}
                            {isEditingInsurance && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-4 space-y-4"
                              >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Provider
                                    </label>
                                    <input
                                      type="text"
                                      name="provider"
                                      value={insuranceForm.provider}
                                      onChange={handleInsuranceFormChange}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                      placeholder="Insurance provider"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Policy Number
                                    </label>
                                    <input
                                      type="text"
                                      name="policyNumber"
                                      value={insuranceForm.policyNumber}
                                      onChange={handleInsuranceFormChange}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                      placeholder="Policy number"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Type
                                    </label>
                                    <select
                                      name="type"
                                      value={insuranceForm.type}
                                      onChange={handleInsuranceFormChange}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value="normal">Normal</option>
                                      <option value="temp">Temporary</option>
                                      <option value="mixed">Mixed</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Start Date
                                    </label>
                                    <input
                                      type="date"
                                      value={moment(insuranceForm.startDate).format('YYYY-MM-DD')}
                                      onChange={(e) => handleInsuranceDateChange('startDate', new Date(e.target.value))}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      End Date
                                    </label>
                                    <input
                                      type="date"
                                      value={moment(insuranceForm.endDate).format('YYYY-MM-DD')}
                                      onChange={(e) => handleInsuranceDateChange('endDate', new Date(e.target.value))}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  {insuranceForm.type === 'mixed' && (
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Coverage Amount (€)
                                      </label>
                                      <input
                                        type="number"
                                        name="coverageAmount"
                                        value={insuranceForm.coverageAmount}
                                        onChange={handleInsuranceFormChange}
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="0"
                                      />
                                    </div>
                                  )}
                                </div>
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={() => setIsEditingInsurance(false)}
                                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={updateInsurance}
                                    disabled={loading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center"
                                  >
                                    {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <IoSave className="mr-2" />}
                                    Save Changes
                                  </button>
                                </div>
                              </motion.div>
                            )}
                          </motion.div>

                          {/* KTEO Card */}
                          <motion.div
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            transition={{ delay: 0.1 }}
                            className={`border rounded-xl p-6 shadow-sm ${
                              isKteoValid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-semibold flex items-center">
                                <IoDocument className="mr-2 text-blue-500" />
                                KTEO
                              </h3>
                              {!isEditingKteo && (
                                <button
                                  onClick={() => setIsEditingKteo(true)}
                                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                                >
                                  <FaEdit className="mr-1 inline" /> Edit
                                </button>
                              )}
                            </div>
                            
                            {vehicle.kteo ? (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">Status</span>
                                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                    isKteoValid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {isKteoValid ? 'Valid' : 'Expired'}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-500">Certificate</span>
                                    <div className="font-medium">{vehicle.kteo.certificateNumber}</div>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Expires</span>
                                    <div className="font-medium">
                                      {moment(vehicle.kteo.expiryDate).format('DD/MM/YYYY')}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <p className="text-gray-600">No KTEO information</p>
                            )}

                            {/* KTEO Edit Form */}
                            {isEditingKteo && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-4 space-y-4"
                              >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Certificate Number
                                    </label>
                                    <input
                                      type="text"
                                      name="certificateNumber"
                                      value={kteoForm.certificateNumber}
                                      onChange={handleKteoFormChange}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                      placeholder="Certificate number"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Status
                                    </label>
                                    <select
                                      name="status"
                                      value={kteoForm.status}
                                      onChange={handleKteoFormChange}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value="valid">Valid</option>
                                      <option value="expired">Expired</option>
                                      <option value="pending">Pending</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Last Inspection Date
                                    </label>
                                    <input
                                      type="date"
                                      value={moment(kteoForm.lastInspectionDate).format('YYYY-MM-DD')}
                                      onChange={(e) => setKteoForm({...kteoForm, lastInspectionDate: new Date(e.target.value)})}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Next Inspection Date
                                    </label>
                                    <input
                                      type="date"
                                      value={moment(kteoForm.nextInspectionDate).format('YYYY-MM-DD')}
                                      onChange={(e) => setKteoForm({...kteoForm, nextInspectionDate: new Date(e.target.value)})}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Expiry Date
                                    </label>
                                    <input
                                      type="date"
                                      value={moment(kteoForm.expiryDate).format('YYYY-MM-DD')}
                                      onChange={(e) => setKteoForm({...kteoForm, expiryDate: new Date(e.target.value)})}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                </div>
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={() => setIsEditingKteo(false)}
                                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={updateKteo}
                                    disabled={loading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center"
                                  >
                                    {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <IoSave className="mr-2" />}
                                    Save Changes
                                  </button>
                                </div>
                              </motion.div>
                            )}
                          </motion.div>

                          {/* Emission Card */}
                          <motion.div
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            transition={{ delay: 0.2 }}
                            className={`border rounded-xl p-6 shadow-sm ${
                              isEmissionValid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-semibold flex items-center">
                                <FaGasPump className="mr-2 text-blue-500" />
                                Emission Card
                              </h3>
                              {!isEditingEmission && (
                                <button
                                  onClick={() => setIsEditingEmission(true)}
                                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                                >
                                  <FaEdit className="mr-1 inline" /> Edit
                                </button>
                              )}
                            </div>
                            
                            {vehicle.emissionCard ? (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">Status</span>
                                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                    isEmissionValid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {isEmissionValid ? 'Valid' : 'Expired'}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-500">Certificate</span>
                                    <div className="font-medium">{vehicle.emissionCard.certificateNumber}</div>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Expires</span>
                                    <div className="font-medium">
                                      {moment(vehicle.emissionCard.expiryDate).format('DD/MM/YYYY')}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <p className="text-gray-600">No emission card information</p>
                            )}

                            {/* Emission Edit Form */}
                            {isEditingEmission && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-4 space-y-4"
                              >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Certificate Number
                                    </label>
                                    <input
                                      type="text"
                                      name="certificateNumber"
                                      value={emissionForm.certificateNumber}
                                      onChange={handleEmissionFormChange}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                      placeholder="Certificate number"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Status
                                    </label>
                                    <select
                                      name="status"
                                      value={emissionForm.status}
                                      onChange={handleEmissionFormChange}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value="valid">Valid</option>
                                      <option value="expired">Expired</option>
                                      <option value="pending">Pending</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Issue Date
                                    </label>
                                    <input
                                      type="date"
                                      value={moment(emissionForm.issueDate).format('YYYY-MM-DD')}
                                      onChange={(e) => setEmissionForm({...emissionForm, issueDate: new Date(e.target.value)})}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Expiry Date
                                    </label>
                                    <input
                                      type="date"
                                      value={moment(emissionForm.expiryDate).format('YYYY-MM-DD')}
                                      onChange={(e) => setEmissionForm({...emissionForm, expiryDate: new Date(e.target.value)})}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                </div>
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={() => setIsEditingEmission(false)}
                                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={updateEmission}
                                    disabled={loading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center"
                                  >
                                    {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <IoSave className="mr-2" />}
                                    Save Changes
                                  </button>
                                </div>
                              </motion.div>
                            )}
                          </motion.div>

                          {/* GPS Card */}
                          <motion.div
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            transition={{ delay: 0.3 }}
                            className={`border rounded-xl p-6 shadow-sm ${
                              vehicle.gps?.installed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-semibold flex items-center">
                                <IoNavigate className="mr-2 text-blue-500" />
                                GPS System
                              </h3>
                              {!isEditingGps && (
                                <button
                                  onClick={() => setIsEditingGps(true)}
                                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                                >
                                  <FaEdit className="mr-1 inline" /> Edit
                                </button>
                              )}
                            </div>
                            
                            {vehicle.gps ? (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">Status</span>
                                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                    vehicle.gps.installed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {vehicle.gps.installed ? 'Installed' : 'Not Installed'}
                                  </span>
                                </div>
                                {vehicle.gps.installed && (
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <span className="text-gray-500">Device ID</span>
                                      <div className="font-medium">{vehicle.gps.deviceId}</div>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Model</span>
                                      <div className="font-medium">{vehicle.gps.model}</div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="text-gray-600">No GPS information</p>
                            )}

                            {/* GPS Edit Form */}
                            {isEditingGps && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-4 space-y-4"
                              >
                                <div className="space-y-4">
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      name="installed"
                                      checked={gpsForm.installed}
                                      onChange={handleGpsFormChange}
                                      className="mr-2"
                                    />
                                    <label className="text-sm font-medium text-gray-700">
                                      GPS Installed
                                    </label>
                                  </div>
                                  {gpsForm.installed && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Device ID
                                        </label>
                                        <input
                                          type="text"
                                          name="deviceId"
                                          value={gpsForm.deviceId}
                                          onChange={handleGpsFormChange}
                                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                          placeholder="Device ID"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Model
                                        </label>
                                        <input
                                          type="text"
                                          name="model"
                                          value={gpsForm.model}
                                          onChange={handleGpsFormChange}
                                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                          placeholder="GPS model"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Installation Date
                                        </label>
                                        <input
                                          type="date"
                                          value={moment(gpsForm.installationDate).format('YYYY-MM-DD')}
                                          onChange={(e) => setGpsForm({...gpsForm, installationDate: new Date(e.target.value)})}
                                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={() => setIsEditingGps(false)}
                                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={updateGps}
                                    disabled={loading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center"
                                  >
                                    {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <IoSave className="mr-2" />}
                                    Save Changes
                                  </button>
                                </div>
                              </motion.div>
                            )}
                          </motion.div>
                        </div>
                      </div>
                    )}

                    {/* Service History Tab */}
                    {activeTab === 'service' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h3 className="text-xl font-semibold text-gray-800">Service History</h3>
                          {!isAddingService && (
                            <button
                              onClick={() => {
                                resetServiceForm();
                                setIsAddingService(true);
                              }}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                            >
                              <FaPlus className="mr-2" /> Add Service Record
                            </button>
                          )}
                        </div>

                        {/* Add Service Form */}
                        {isAddingService && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="bg-blue-50 border border-blue-200 rounded-xl p-6"
                          >
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="font-semibold text-blue-800">Add New Service Record</h4>
                              <button
                                onClick={() => setIsAddingService(false)}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <FaTimes />
                              </button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Service Type
                                </label>
                                <select
                                  name="type"
                                  value={serviceForm.type}
                                  onChange={handleServiceFormChange}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  required
                                >
                                  <option value="">Select service type</option>
                                  <option value="regular maintenance">Regular Maintenance</option>
                                  <option value="repair">Repair</option>
                                  <option value="inspection">Inspection</option>
                                  <option value="modification">Modification</option>
                                  <option value="cleaning">Cleaning</option>
                                  <option value="other">Other</option>
                                </select>
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Service Date
                                </label>
                                <input
                                  type="date"
                                  name="date"
                                  value={serviceForm.date ? moment(serviceForm.date).format('YYYY-MM-DD') : ''}
                                  onChange={(e) => setServiceForm({ ...serviceForm, date: new Date(e.target.value) })}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  required
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Mileage (km)
                                </label>
                                <input
                                  type="number"
                                  name="mileage"
                                  value={serviceForm.mileage}
                                  onChange={handleServiceFormChange}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  placeholder="Current mileage"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Cost (€)
                                </label>
                                <input
                                  type="number"
                                  name="cost"
                                  value={serviceForm.cost}
                                  onChange={handleServiceFormChange}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  placeholder="0.00"
                                  step="0.01"
                                  required
                                />
                              </div>
                              
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Description
                                </label>
                                <textarea
                                  name="description"
                                  value={serviceForm.description}
                                  onChange={handleServiceFormChange}
                                  rows="3"
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  placeholder="Service details..."
                                />
                              </div>
                            </div>
                            
                            <div className="flex justify-end mt-4">
                              <button
                                onClick={addServiceRecord}
                                disabled={loading}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center"
                              >
                                {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <IoSave className="mr-2" />}
                                Save Service Record
                              </button>
                            </div>
                          </motion.div>
                        )}

                        {/* Edit Service Form */}
                        {isEditingService && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="bg-yellow-50 border border-yellow-200 rounded-xl p-6"
                          >
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="font-semibold text-yellow-800">Edit Service Record</h4>
                              <button
                                onClick={() => {
                                  setIsEditingService(false);
                                  setSelectedService(null);
                                  resetServiceForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <FaTimes />
                              </button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Service Type
                                </label>
                                <select
                                  name="type"
                                  value={serviceForm.type}
                                  onChange={handleServiceFormChange}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  required
                                >
                                  <option value="">Select service type</option>
                                  <option value="regular maintenance">Regular Maintenance</option>
                                  <option value="repair">Repair</option>
                                  <option value="inspection">Inspection</option>
                                  <option value="modification">Modification</option>
                                  <option value="cleaning">Cleaning</option>
                                  <option value="other">Other</option>
                                </select>
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Service Date
                                </label>
                                <input
                                  type="date"
                                  name="date"
                                  value={serviceForm.date ? moment(serviceForm.date).format('YYYY-MM-DD') : ''}
                                  onChange={(e) => setServiceForm({ ...serviceForm, date: new Date(e.target.value) })}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  required
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Mileage (km)
                                </label>
                                <input
                                  type="number"
                                  name="mileage"
                                  value={serviceForm.mileage}
                                  onChange={handleServiceFormChange}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  placeholder="Current mileage"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Cost (€)
                                </label>
                                <input
                                  type="number"
                                  name="cost"
                                  value={serviceForm.cost}
                                  onChange={handleServiceFormChange}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  placeholder="0.00"
                                  step="0.01"
                                  required
                                />
                              </div>
                              
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Description
                                </label>
                                <textarea
                                  name="description"
                                  value={serviceForm.description}
                                  onChange={handleServiceFormChange}
                                  rows="3"
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  placeholder="Service details..."
                                />
                              </div>
                            </div>
                            
                            <div className="flex justify-end mt-4">
                              <button
                                onClick={updateServiceRecord}
                                disabled={loading}
                                className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:bg-yellow-300 flex items-center"
                              >
                                {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <IoSave className="mr-2" />}
                                Update Service Record
                              </button>
                            </div>
                          </motion.div>
                        )}

                        {/* Service Records List */}
                        <div className="space-y-4">
                          {serviceHistory.length > 0 ? (
                            serviceHistory
                              .sort((a, b) => new Date(b.date) - new Date(a.date))
                              .map((record, index) => (
                                <motion.div
                                  key={record._id || index}
                                  variants={cardVariants}
                                  initial="hidden"
                                  animate="visible"
                                  transition={{ delay: index * 0.1 }}
                                  className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-3 mb-2">
                                        <h4 className="font-semibold text-gray-800 capitalize">{record.type}</h4>
                                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                                          €{parseFloat(record.cost).toFixed(2)}
                                        </span>
                                      </div>
                                      <p className="text-gray-600 mb-2">{record.description}</p>
                                      <div className="flex items-center text-sm text-gray-500">
                                        <IoCalendar className="mr-1" />
                                        {moment(record.date).format('DD/MM/YYYY')}
                                        {record.mileage && (
                                          <>
                                            <span className="mx-2">•</span>
                                            <IoSpeedometer className="mr-1" />
                                            {record.mileage} km
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2 ml-4">
                                      <button
                                        onClick={() => editService(record)}
                                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Edit service record"
                                      >
                                        <FaEdit />
                                      </button>
                                      <button
                                        onClick={() => deleteServiceRecord(record._id)}
                                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Delete service record"
                                      >
                                        <FaTrash />
                                      </button>
                                    </div>
                                  </div>
                                </motion.div>
                              ))
                          ) : (
                            <div className="text-center py-12">
                              <FaTools className="mx-auto text-4xl text-gray-300 mb-4" />
                              <p className="text-gray-500">No service records found</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Attachments Tab */}
                    {activeTab === 'attachments' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h3 className="text-xl font-semibold text-gray-800">Attachments</h3>
                          {!isAddingAttachment && (
                            <button
                              onClick={() => setIsAddingAttachment(true)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                            >
                              <FaPlus className="mr-2" /> Add Attachment
                            </button>
                          )}
                        </div>

                        {/* Add Attachment Form */}
                        {isAddingAttachment && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="bg-blue-50 border border-blue-200 rounded-xl p-6"
                          >
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="font-semibold text-blue-800">Add New Attachment</h4>
                              <button
                                onClick={() => setIsAddingAttachment(false)}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <FaTimes />
                              </button>
                            </div>
                            
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  File
                                </label>
                                <input
                                  type="file"
                                  onChange={(e) => setAttachmentForm({ ...attachmentForm, file: e.target.files[0] })}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  required
                                />
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Category
                                  </label>
                                  <select
                                    value={attachmentForm.category}
                                    onChange={(e) => setAttachmentForm({ ...attachmentForm, category: e.target.value })}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="other">Other</option>
                                    <option value="service">Service Records</option>
                                    <option value="insurance">Insurance Documents</option>
                                    <option value="registration">Registration Papers</option>
                                    <option value="tax">Tax Documents</option>
                                    <option value="accident">Accident Reports</option>
                                    <option value="kteo">KTEO Documents</option>
                                    <option value="emission">Emission Card Documents</option>
                                    <option value="damage">Damage Reports</option>
                                  </select>
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Description
                                  </label>
                                  <input
                                    type="text"
                                    value={attachmentForm.description}
                                    onChange={(e) => setAttachmentForm({ ...attachmentForm, description: e.target.value })}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Brief description..."
                                  />
                                </div>
                              </div>
                              
                              <div className="flex justify-end">
                                <button
                                  onClick={async () => {
                                    if (!attachmentForm.file) {
                                      setError('Please select a file to upload');
                                      return;
                                    }
                                    
                                    setLoading(true);
                                    setError(null);
                                    setSuccessMessage(null);
                                    
                                    try {
                                      const formData = new FormData();
                                      formData.append('file', attachmentForm.file);
                                      formData.append('description', attachmentForm.description);
                                      formData.append('category', attachmentForm.category);
                                      
                                      await cars.uploadAttachment(car._id, vehicle._id, formData);
                                      setSuccessMessage('Attachment added successfully');
                                      
                                      setAttachmentForm({ file: null, description: '', category: 'other' });
                                      setIsAddingAttachment(false);
                                      
                                      await fetchAttachments();
                                    } catch (err) {
                                      console.error('Error adding attachment:', err);
                                      setError('Failed to add attachment: ' + (err.message || 'Unknown error'));
                                    } finally {
                                      setLoading(false);
                                    }
                                  }}
                                  disabled={loading}
                                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center"
                                >
                                  {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <IoSave className="mr-2" />}
                                  Save Attachment
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        )}

                        {/* Edit Attachment Form */}
                        {isEditingAttachment && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="bg-yellow-50 border border-yellow-200 rounded-xl p-6"
                          >
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="font-semibold text-yellow-800">Edit Attachment</h4>
                              <button
                                onClick={() => {
                                  setIsEditingAttachment(false);
                                  setSelectedAttachment(null);
                                  setAttachmentForm({ file: null, description: '', category: 'other' });
                                }}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <FaTimes />
                              </button>
                            </div>
                            
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Replace File (optional)
                                </label>
                                <input
                                  type="file"
                                  onChange={(e) => setAttachmentForm({ ...attachmentForm, file: e.target.files[0] })}
                                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                                <p className="text-xs text-gray-500 mt-1">Leave empty to keep current file</p>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Category
                                  </label>
                                  <select
                                    value={attachmentForm.category}
                                    onChange={(e) => setAttachmentForm({ ...attachmentForm, category: e.target.value })}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="other">Other</option>
                                    <option value="service">Service Records</option>
                                    <option value="insurance">Insurance Documents</option>
                                    <option value="registration">Registration Papers</option>
                                    <option value="tax">Tax Documents</option>
                                    <option value="accident">Accident Reports</option>
                                    <option value="kteo">KTEO Documents</option>
                                    <option value="emission">Emission Card Documents</option>
                                    <option value="damage">Damage Reports</option>
                                  </select>
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Description
                                  </label>
                                  <input
                                    type="text"
                                    value={attachmentForm.description}
                                    onChange={(e) => setAttachmentForm({ ...attachmentForm, description: e.target.value })}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Brief description..."
                                  />
                                </div>
                              </div>
                              
                              <div className="flex justify-end">
                                <button
                                  onClick={updateAttachment}
                                  disabled={loading}
                                  className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:bg-yellow-300 flex items-center"
                                >
                                  {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <IoSave className="mr-2" />}
                                  Update Attachment
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        )}

                        {/* Attachments Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {attachments.length > 0 ? (
                            attachments
                              .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate))
                              .map((attachment, index) => (
                                <motion.div
                                  key={attachment._id || index}
                                  variants={cardVariants}
                                  initial="hidden"
                                  animate="visible"
                                  transition={{ delay: index * 0.1 }}
                                  className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all"
                                >
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center space-x-2">
                                      <IoDocument className="text-blue-500 text-xl" />
                                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium capitalize">
                                        {attachment.category}
                                      </span>
                                    </div>
                                    <div className="flex space-x-1">
                                      <button
                                        onClick={() => window.open(`https://rent-server.create-site.gr${attachment.fileUrl}`, '_blank')}
                                        className="p-1 text-blue-600 hover:text-blue-800"
                                        title="View"
                                      >
                                        <FaEye />
                                      </button>
                                      <button
                                        onClick={() => editAttachment(attachment)}
                                        className="p-1 text-yellow-600 hover:text-yellow-800"
                                        title="Edit"
                                      >
                                        <FaEdit />
                                      </button>
                                      <button
                                        onClick={() => deleteAttachment(attachment._id)}
                                        className="p-1 text-red-600 hover:text-red-800"
                                        title="Delete"
                                      >
                                        <FaTrash />
                                      </button>
                                    </div>
                                  </div>
                                  
                                  <h4 className="font-medium text-gray-800 mb-1 truncate">
                                    {attachment.description || attachment.fileName}
                                  </h4>
                                  
                                  <p className="text-sm text-gray-500 mb-2 truncate">
                                    {attachment.fileName}
                                  </p>
                                  
                                  <p className="text-xs text-gray-400">
                                    {moment(attachment.uploadDate).format('DD/MM/YYYY HH:mm')}
                                  </p>
                                </motion.div>
                              ))
                          ) : (
                            <div className="col-span-full text-center py-12">
                              <IoDocument className="mx-auto text-4xl text-gray-300 mb-4" />
                              <p className="text-gray-500">No attachments found</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default VehicleDetailsModal;